import { AttachmentDetail_SignatureStatus } from '@policyfly/protobuf/patrick'
import { InputData_Protobuf_Bucket } from '@policyfly/protobuf/pfschema'
import { computed } from 'vue'

import { useAppContextStore } from '@/stores/appContext'
import { useApplicationStore } from '@/stores/application'
import { useAttachmentStore } from '@/stores/attachment'
import { useDiffStore } from '@/stores/diff'
import { usePolicyStore } from '@/stores/policy'
import { useProtobufStore } from '@/stores/protobuf'
import { useSettingsStore } from '@/stores/settings'

import { convertChangeList } from './protobuf'
import {
  combineRemovedArrayItems,
  createAddressComputedMap,
  createLocationArrayComputedMap,
  extractArrayDiffs,
  extractObjectDiffs,
} from '@/utils/diffHelpers'
import { formatDate } from '@/utils/formatter'
import { getInputData } from '@/utils/inputData'
import { getNestedResponse } from '@/utils/responses'

import type { NestedResponseObject, PayloadArray } from '@/lib/Payload'
import type { CreateMapFn } from '@/stores/programData'
import type { ChangeList } from '@policyfly/protobuf'
import type {
  SchemaSettings_ProgramData_Field_FirstYearInBusiness,
  SchemaSettings_ProgramData_Field_Schedule,
} from '@policyfly/protobuf/pfschema'
import type { APIPayloadResponse } from '@policyfly/utils/types'
import type { ComputedRef } from 'vue'

export function createSignedProposalComputed (documentType: string): ComputedRef<boolean> {
  return computed(() => {
    const attachmentStore = useAttachmentStore()
    const docs = attachmentStore.signableDocuments
    if (!docs) return false
    return docs.documents.some((d) => d.attachment.documentType === documentType &&
      d.attachment.signatureStatus === AttachmentDetail_SignatureStatus.SIGNATURE_STATUS_APPROVED)
  })
}

export function createFirstYearInBusinessComputed (params: SchemaSettings_ProgramData_Field_FirstYearInBusiness): ComputedRef<number> {
  return computed(() => {
    const protobufStore = useProtobufStore()
    let yearsInBusiness = protobufStore.getValue<number>({ path: 'history.yearsInBusiness' }, { defaultBucket: InputData_Protobuf_Bucket.BUCKET_PS }) ?? 0
    if (params.includeZero && yearsInBusiness === 0) yearsInBusiness = 1
    const effectiveYear = protobufStore.getValue<number>({ path: 'effective.year' }, { defaultBucket: InputData_Protobuf_Bucket.BUCKET_PS }) ?? 0
    return effectiveYear - yearsInBusiness
  })
}

export function createScheduleComputed (params: SchemaSettings_ProgramData_Field_Schedule): ComputedRef<PayloadArray> {
  return computed(() => {
    const changes = params.data && getInputData<ChangeList>(params.data, { defaultBucket: InputData_Protobuf_Bucket.BUCKET_PS })
    return convertChangeList(changes, params.config)
  })
}

export function createTermComputed (): ComputedRef<{ v: string } | null> {
  return computed(() => {
    const appContextStore = useAppContextStore()
    if (!appContextStore.loadedApplicationData) return null
    const isInProgress = appContextStore.isInProgress
    const policyStore = usePolicyStore()
    const { effectiveDate, expirationDate } = appContextStore.loadedApplicationData
    const { effective: policyEffectiveDate, expiration: policyExpirationDate } = policyStore.currentCoveragePeriod
    const usedPolicyEffective = isInProgress ? null : policyEffectiveDate
    const usedPolicyExpiration = isInProgress ? null : policyExpirationDate
    const effective = usedPolicyEffective || effectiveDate || getNestedResponse('startDate') || getNestedResponse('effectiveDate') || null
    const expiration = usedPolicyExpiration || expirationDate || null
    const formatter = (date: string): string => formatDate(date)
    let term = ''
    if (effective) {
      term += `${formatter(effective)} - `
      if (expiration) term += formatter(expiration)
      else {
        let [y, m, d] = effective.split('T')[0].split('-')
        y++
        if (m === '02' && d === '29') d = '28'
        term += formatter(`${y}-${m}-${d}`)
      }
    }
    return { v: term }
  })
}

function contactPersonFormatter (contactPerson?: { contactPerson?: APIPayloadResponse<string>, title?: APIPayloadResponse<string>, phone?: APIPayloadResponse<string> } | null): (string | null)[] {
  if (!contactPerson) return []
  const { contactPerson: person = { v: null }, title = { v: null }, phone = { v: null } } = contactPerson
  return [
    person.v,
    title.v,
    phone.v,
  ]
}
function additionalNamedInsuredFormatter (additionalNamedInsured: { name?: { v: string }, v: string }[]): string[] {
  if (!additionalNamedInsured) return []
  return additionalNamedInsured.map((n) => n.name ? n.name.v : n.v)
}

export const createLegacyMap: CreateMapFn = () => {
  const contactPersonRaw = computed(() => {
    const appContextStore = useAppContextStore()
    const nestedResponseObjects = appContextStore.nestedResponseObjects
    if (!nestedResponseObjects) return null
    return {
      contactPerson: nestedResponseObjects.contactPerson || {},
      title: nestedResponseObjects.title || {},
      phone: nestedResponseObjects.phone || {},
    }
  })

  const additionalNamedInsuredRaw = computed(() => {
    const appContextStore = useAppContextStore()
    const diffStore = useDiffStore()
    const nestedResponseObjects = appContextStore.nestedResponseObjects
    if (!nestedResponseObjects) return null
    const allANIs = combineRemovedArrayItems(
      nestedResponseObjects.additionalNamedInsured as NestedResponseObject[],
      'additionalNamedInsured',
      diffStore.nestedRemovedDiffs,
      // @ts-expect-error(fixable): Name not known to be PayloadResponse
      ({ name = {}, ...otherKeys }) => ({ name: { k: name.k, uuid4: name.uuid4, v: null }, ...otherKeys }),
    )
    const { coverages } = useSettingsStore()
    if (!coverages?.length || !allANIs) return allANIs
    return allANIs.filter((ani) => {
      return coverages.some((c) => {
        // @ts-expect-error(fixable): May not exist
        const hasCoverage: boolean = ani[c.label]?.v
        return hasCoverage && getInputData<boolean>(c.data!, { defaultBucket: InputData_Protobuf_Bucket.BUCKET_PS })
      })
    })
  })

  return {
    ...createAddressComputedMap('mainAddress', { path: '', source: 'responses' }),
    ...createLocationArrayComputedMap('locations', 'locations.data'),
    contactPersonRaw,
    contactPerson: computed(() => {
      // @ts-expect-error(fixable): Shape is different
      return contactPersonFormatter(contactPersonRaw.value)
    }),
    contactPersonDiff: computed(() => {
      const diffStore = useDiffStore()
      return extractObjectDiffs(contactPersonRaw.value, diffStore.diffMap, contactPersonFormatter)
    }),
    additionalNamedInsuredRaw,
    additionalNamedInsured: computed(() => {
      // @ts-expect-error(fixable): Shape is different
      return additionalNamedInsuredFormatter(additionalNamedInsuredRaw.value)
    }),
    additionalNamedInsuredDiff: computed(() => {
      const diffStore = useDiffStore()
      return extractArrayDiffs(
        additionalNamedInsuredRaw.value,
        diffStore.diffMap,
        // @ts-expect-error(fixable): Can return null
        additionalNamedInsuredFormatter,
        'name',
      )
    }),
    wordings: computed(() => {
      const applicationStore = useApplicationStore()
      return applicationStore.wordings.map((w) => {
        // nest all meta keys in { v }
        const meta = Object.entries(w.meta).reduce<Record<string, { v: string | number | boolean }>>((acc, [k, v]) => {
          acc[k] = { v }
          return acc
        }, {})
        return {
          text: { v: w.formatted || w.text },
          meta,
        }
      }).sort((a, b) => {
        const aSectionOrder = a.meta.sectionOrder?.v
        const bSectionOrder = b.meta.sectionOrder?.v
        if (aSectionOrder === bSectionOrder) {
          const aOrder = a.meta.order?.v
          const bOrder = b.meta.order?.v
          if (aOrder === bOrder) return 0
          return (aOrder! < bOrder!) ? -1 : 1
        }
        return (aSectionOrder! < bSectionOrder!) ? -1 : 1
      })
    }),
    wordingsDiffMap: computed(() => {
      const diffStore = useDiffStore()
      return diffStore.diffMap
    }),
  }
}
